import {
  IconButton,
  Menu,
  MenuItem,
  MenuList,
  Stack,
  Typography,
  Dialog,
  AppBar,
  Toolbar,
  Button,
  DialogContent,
  Grid2,
  Backdrop,
  CircularProgress,
} from '@mui/material'
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state'
import { PopupState as PopupStateType } from 'material-ui-popup-state/hooks'
import { Fragment, useState, useEffect } from 'react'
import {
  FileDownloadRounded,
  MoreVertRounded,
  RemoveRedEyeRounded,
  ContentCut,
  PersonOutlineRounded,
} from '@mui/icons-material'
import {
  client,
  QcTimepointContainerSubmission,
  TimepointContainerSubmissionExamMetadataResponse,
  QcTaskTimepointSchema,
  QcTaskState,
} from '@common/config/api/client'
import DeleteModal from '../modals/DeleteModal'
import { SPACING } from '@common/theme/spacing'
import { SIZING } from '@common/theme/sizing'
import SeriesModal from '../modals/SeriesModal'
import { useDownloadExamMetadataMutation } from '@features/qc_task_details_view/hooks/useDownloadExamMetadataMutation'
import { useParams } from 'react-router-dom'
import { launchDicomViewerForQC } from '@common/utils/ohifUtils'
import { useExtendedAuth } from '@auth/hooks/useExtendedAuth'
import ReviewDicomTagsModal from '../modals/ReviewDicomTagsModal'
import { toast } from 'sonner'
import Toast from '@common/components/Toast'
import PHIRedactionStep from '@features/subject/components/PHIRedactionStep'
import { SitePermission } from '@auth/permissionsEnum'
import { useCombinedPermissions } from '@auth/hooks/useCombinedPermissions'
import { useQCTask } from '@features/qc_task_details_view/hooks/useQCTask'
import { useGetQcTaskUploader } from '@features/qc_task_details_view/hooks/useGetQcTaskUploader'
import UploaderDetailsModal from '@common/components/UploaderDetailsModal'
import {
  CriteriaCheckResultsDialog,
  CriteriaCheckResultsMenuItem,
} from '@features/subject/components/CriteriaCheckResultsMenuItem'

export default function ExamDetailPopupMenuList({
  qcTaskOrder,
  qcTaskStatus,
  timepointSubmissionId,
  container,
  exam,
  showViewerOption = true,
}: {
  qcTaskOrder: number
  qcTaskStatus: QcTaskState
  timepointSubmissionId: string
  container?: QcTimepointContainerSubmission
  exam?: TimepointContainerSubmissionExamMetadataResponse
  showViewerOption?: boolean
}) {
  const { qcTaskId, studyId } = useParams()
  const [loading, setLoading] = useState(false)
  const [phiRedactionOpen, setPhiRedactionOpen] = useState(false)
  const [uploaderModalOpen, setUploaderModalOpen] = useState(false)
  const [criteriaCheckResultsOpen, setCriteriaCheckResultsOpen] = useState(false)
  const { user } = useExtendedAuth()
  const { hasPermission } = useCombinedPermissions()
  const [qcTaskData, setQcTaskData] = useState<QcTaskTimepointSchema | null>(null)
  const {
    data: uploaderData,
    isLoading: uploaderLoading,
    isError: uploaderError,
  } = useGetQcTaskUploader({ study_id: studyId, qc_task_id: qcTaskId }, uploaderModalOpen)

  const { mutateAsync: downloadExamMetadata } = useDownloadExamMetadataMutation()

  const { data: qcTask } = useQCTask(['QCTask'], studyId, qcTaskId)

  useEffect(() => {
    if (qcTask?.data) {
      console.log('QC Task Data:', qcTask.data)
      setQcTaskData(qcTask.data)
    }
  }, [qcTask])

  const handlePhiRedactionClose = () => {
    setPhiRedactionOpen(false)
  }

  if (!studyId || !user?.access_token) {
    toast(
      <Toast
        message="Missing required parameters for QC task viewer. Please ensure study ID and authentication token are provided."
        variant="error"
      />,
    )
    return false
  }

  const handledDownloadExamMetadata = async (popupState: PopupStateType) => {
    if (!container || !qcTaskId || !studyId) return

    // Close the menu
    popupState.close()

    // Show loading state
    setLoading(true)
    toast.loading('Preparing download...', { id: 'exam-download' })

    downloadExamMetadata({
      path: {
        qc_task_id: qcTaskId,
      },

      query: {
        timepoint_submission_id: timepointSubmissionId,
        study_id: studyId,
        timepoint_container_submission_id: container?.container_submission_id,
      },
    })
      .then(async (response) => {
        try {
          const fileUrl = response.url
          if (fileUrl) {
            const fileResponse = await client.instance.get(fileUrl, {
              responseType: 'blob',
            })
            if (fileResponse.status !== 200) throw new Error('Failed to fetch file')
            const blob = fileResponse.data
            const blobUrl = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = blobUrl
            link.download = fileUrl.split('/').pop()?.split('?').shift() || 'exam_metadata.zip'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            toast.success('Download started successfully', { id: 'exam-download' })
          } else {
            throw new Error('Failed to fetch file')
          }
        } catch (error) {
          toast.error(`Error downloading the file: ${error}`, { id: 'exam-download' })
          console.error('Error downloading the file:', error)
        } finally {
          setLoading(false)
        }
      })
      .catch((error) => {
        toast.error('Failed to generate download URL', { id: 'exam-download' })
        console.error('Error fetching download URL:', error)
        setLoading(false)
      })
  }

  return (
    <PopupState variant="popover">
      {(popupState: PopupStateType) => (
        <Fragment>
          <IconButton {...bindTrigger(popupState)}>
            <MoreVertRounded
              sx={{
                color: 'text.secondary',
                width: 20,
                height: 20,
              }}
            />
          </IconButton>
          <Menu
            {...bindMenu(popupState)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            data-testid="popup-menu-list"
          >
            <MenuList
              sx={{
                width: 220,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                paddingY: SPACING.examDetailPopupMenuListSpacing,
                outline: 'none',
              }}
            >
              {showViewerOption && (
                <MenuItem
                  sx={{
                    height: 36,
                    width: '100%',
                    paddingY: SPACING.examDetailPopupMenuListSpacing,
                  }}
                  onClick={() => {
                    if (studyId && qcTaskId && user?.access_token) {
                      launchDicomViewerForQC(
                        studyId,
                        qcTaskId,
                        user.access_token,
                        exam?.series[0]?.series_uid || undefined,
                        true,
                        false,
                        exam?.study_uid || undefined,
                        Boolean(exam?.study_uid),
                      )
                    }
                  }}
                  data-testid="popup-menu-list-item-reopen"
                >
                  <Stack direction="row" spacing={SPACING.popupMenuIconSpacing} alignItems="center">
                    <RemoveRedEyeRounded
                      sx={{
                        color: 'text.secondary',
                      }}
                    />
                    <Typography>Open in viewer</Typography>
                  </Stack>
                </MenuItem>
              )}
              <MenuItem
                sx={{
                  height: 36,
                  width: '100%',
                  paddingY: SPACING.examDetailPopupMenuListSpacing,
                }}
                data-testid="popup-menu-list-item-reset"
              >
                <ReviewDicomTagsModal exam={exam} popupState={popupState} readOnly={qcTaskStatus === 'COMPLETE'} />
              </MenuItem>
              {container?.container_submission_id && studyId && qcTaskId && (
                <CriteriaCheckResultsMenuItem
                  onClick={() => {
                    setCriteriaCheckResultsOpen(true)
                    popupState.close()
                  }}
                />
              )}
              <MenuItem
                sx={{
                  height: 36,
                  width: '100%',
                  paddingY: SPACING.examDetailPopupMenuListSpacing,
                }}
                onClick={() => handledDownloadExamMetadata(popupState)}
                disabled={loading}
                data-testid="popup-menu-list-item-reset"
              >
                <Stack direction="row" spacing={SPACING.popupMenuIconSpacing} alignItems="center">
                  <FileDownloadRounded
                    sx={{
                      color: 'text.secondary',
                    }}
                  />
                  <Typography>Download</Typography>
                </Stack>
              </MenuItem>
              <MenuItem
                sx={{
                  height: 36,
                  width: '100%',
                  paddingY: SPACING.examDetailPopupMenuListSpacing,
                }}
                onClick={() => {
                  setUploaderModalOpen(true)
                  popupState.close()
                }}
                data-testid="popup-menu-list-item-uploaded-by"
              >
                <Stack direction="row" spacing={SPACING.popupMenuIconSpacing} alignItems="center">
                  <PersonOutlineRounded
                    sx={{
                      color: 'text.secondary',
                    }}
                  />
                  <Typography>Uploaded by</Typography>
                </Stack>
              </MenuItem>
              <MenuItem
                sx={{
                  height: 36,
                  width: '100%',
                  paddingY: SPACING.examDetailPopupMenuListSpacing,
                }}
                data-testid="popup-menu-list-item-reset"
              >
                <SeriesModal
                  container_submission_id={container?.container_submission_id}
                  exam={exam}
                  popupState={popupState}
                  timepointSubmissionId={timepointSubmissionId}
                />
              </MenuItem>
              {qcTaskStatus !== 'COMPLETE' && qcTaskOrder == 1 && (
                <MenuItem
                  sx={{
                    height: 36,
                    width: '100%',
                    paddingY: SPACING.examDetailPopupMenuListSpacing,
                  }}
                  data-testid="popup-menu-list-item-reset"
                >
                  <DeleteModal
                    container_submission_id={container?.container_submission_id}
                    exam={exam}
                    popupState={popupState}
                  />
                </MenuItem>
              )}
              {hasPermission(SitePermission.ALL) ||
              (hasPermission(SitePermission.REDACT_PHI) &&
                timepointSubmissionId &&
                qcTaskData?.task_state !== 'COMPLETE' &&
                qcTaskData?.order === 1) ? (
                <MenuItem
                  sx={{
                    height: 36,
                    width: '100%',
                    paddingY: SPACING.examDetailPopupMenuListSpacing,
                  }}
                  onClick={() => {
                    setPhiRedactionOpen(true)
                    popupState.close()
                  }}
                  data-testid="popup-menu-list-item-phi-redaction"
                >
                  <Stack direction="row" spacing={SPACING.popupMenuIconSpacing} alignItems="center">
                    <ContentCut
                      sx={{
                        color: 'text.secondary',
                      }}
                    />
                    <Typography>Redact PHI</Typography>
                  </Stack>
                </MenuItem>
              ) : null}
            </MenuList>
          </Menu>

          {/* Global loading backdrop */}
          <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
            <CircularProgress color="inherit" />
          </Backdrop>

          <UploaderDetailsModal
            open={uploaderModalOpen}
            onClose={() => setUploaderModalOpen(false)}
            data={uploaderData}
            isLoading={uploaderLoading}
            isError={uploaderError}
          />

          {container?.container_submission_id && studyId && qcTaskId && (
            <CriteriaCheckResultsDialog
              open={criteriaCheckResultsOpen}
              onClose={() => setCriteriaCheckResultsOpen(false)}
              fetchParams={{
                mode: 'qc',
                studyId,
                qcTaskId,
                timepointSubmissionId,
                containerSubmissionId: container.container_submission_id,
              }}
            />
          )}

          {/* Add PHI Redaction Dialog */}
          <Dialog
            fullScreen
            open={phiRedactionOpen}
            onClose={handlePhiRedactionClose}
            aria-labelledby="phi-redaction-dialog-title"
            aria-describedby="phi-redaction-dialog-description"
          >
            <AppBar position="static" sx={{ bgcolor: 'white', boxShadow: 2, zIndex: 10 }}>
              <Toolbar sx={{ justifyContent: 'space-between' }}>
                <Stack>
                  <Typography id="phi-redaction-dialog-title" color="text.primary" variant="subtitle2">
                    PHI Redaction
                  </Typography>
                  <Typography id="phi-redaction-dialog-description" color="text.secondary" variant="body2">
                    {exam?.study_description}
                  </Typography>
                </Stack>
                <Button
                  onClick={handlePhiRedactionClose}
                  color="secondary"
                  variant="contained"
                  size="large"
                  aria-label="Close PHI redaction dialog"
                >
                  Close
                </Button>
              </Toolbar>
            </AppBar>
            <DialogContent sx={{ p: SPACING.noSpacing, bgcolor: '#F0F4F5' }}>
              <Grid2 container height={SIZING.minWidth}>
                <Grid2 size={SIZING.gridSizeXxl} pt={SPACING.spacingXxl}>
                  {qcTaskData && (
                    <PHIRedactionStep
                      studyId={studyId!}
                      subjectId={qcTaskData.subject.subject_id}
                      timepointSubmissionId={timepointSubmissionId}
                    />
                  )}
                </Grid2>
              </Grid2>
            </DialogContent>
          </Dialog>
        </Fragment>
      )}
    </PopupState>
  )
}
